// /opt/ptg01-v1/front-extranet/components/AuthGuard.tsx
// V2 — cookie-first : auth validée via GET /api/me (cookie httpOnly), pas via localStorage token.
// Le `user` metadata reste en localStorage pour UX (rendu instantané sans flash de "Chargement…").
// Si /api/me répond 200 → cookie valide. Si 401 → cookie expiré/absent → redirect login.
//
// 2026-09-22 — « je n'ai pas pu demander » n'est pas « tu n'es pas connecté ». La validation
// initiale renvoyait vers l'écran de connexion sur TOUTE erreur, y compris une requête coupée
// par un réseau mobile capricieux : le consultant était éjecté en pleine saisie alors que sa
// session était parfaitement valide côté serveur (aucun événement de déconnexion en base).
// Constaté sur Safari iOS pendant le parcours d'intégration. Une panne réseau est désormais
// réessayée, puis présentée comme telle — la session n'est détruite que sur un vrai 401/403.
'use client';

import { useEffect, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { fetchExtranet } from '@/lib/fetchExtranet';
import { useAuth } from '@/lib/AuthContext';

interface AuthGuardProps {
    children: React.ReactNode;
    requireActive?: boolean;
}

type AuthState = 'checking' | 'authorized' | 'unauthorized' | 'injoignable';

export default function AuthGuard({ children, requireActive = true }: AuthGuardProps) {
    const router = useRouter();
    const pathname = usePathname();
    const { setUser } = useAuth();
    const [state, setState] = useState<AuthState>('checking');

    const [essai, setEssai] = useState(0);

    // Validation initiale : cookie httpOnly envoyé auto via credentials: 'include'
    useEffect(() => {
        let cancelled = false;

        async function validate() {
            try {
                const res = await fetchExtranet('/api/me');
                if (cancelled) return;

                if (res.status === 401 || res.status === 403) {
                    setUser(null);                            // nettoie context + localStorage
                    router.replace('/auth/login');
                    setState('unauthorized');
                    return;
                }

                const json = await res.json();
                if (!json.success || !json.data) {
                    router.replace('/auth/login');
                    setState('unauthorized');
                    return;
                }

                // Extract user from response (API returns data: { user: {...}, documents: [...] })
                const user = json.data.user || json.data;

                // Hydrate le context (qui persiste aussi en localStorage pour UX)
                setUser(user);

                // S'assure que le cookie CSRF existe (session pré-CSRF ou cookie expiré)
                if (!document.cookie.match(/(?:^|;\s*)nx_csrf=/)) {
                    fetchExtranet('/api/auth/csrf-token').catch(() => {});
                }

                // Email not verified → bloque tout
                if (!user.emailVerified) {
                    router.replace('/auth/login');
                    setState('unauthorized');
                    return;
                }

                // Must change password
                if (user.mustChangePassword && pathname !== '/auth/change-password') {
                    router.replace('/auth/change-password');
                    setState('unauthorized');
                    return;
                }

                // Suspended
                if (String(user.status || '').endsWith('_SUSPENDED')) {
                    setUser(null);
                    router.replace('/auth/login');
                    setState('unauthorized');
                    return;
                }

                // Require ACTIVE → redirect onboarding sinon
                if (requireActive && user.status !== 'ACTIVE') {
                    router.replace('/onboarding');
                    setState('unauthorized');
                    return;
                }

                setState('authorized');
            } catch {
                // La requête n'a pas abouti : réseau, serveur qui redémarre, onglet réveillé.
                // On ne sait RIEN de la session — surtout pas qu'elle est invalide. Deux
                // nouvelles tentatives espacées, puis un écran qui propose de réessayer.
                if (cancelled) return;
                if (essai < 2) {
                    const delai = 800 * (essai + 1);
                    setTimeout(() => { if (!cancelled) setEssai(n => n + 1); }, delai);
                    return;
                }
                setState('injoignable');
            }
        }

        validate();
        return () => { cancelled = true; };
    }, [router, requireActive, pathname, setUser, essai]);

    // Poll session validity every 5s (détecte login depuis autre appareil → revoke)
    useEffect(() => {
        if (state !== 'authorized') return;
        const check = async () => {
            try {
                const res = await fetchExtranet('/api/me');
                if (res.status === 401) {
                    setUser(null);
                    // Session invalidée côté serveur : rechargement complet pour
                    // repartir d'un état client vierge.
                    window.location.assign(new URL('/auth/login?reason=session', window.location.origin));
                }
            } catch { /* network error — silent */ }
        };
        const interval = setInterval(check, 5000);
        return () => clearInterval(interval);
    }, [state, setUser]);

    if (state === 'injoignable') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white px-6 text-center"
                data-testid="auth-injoignable">
                <p className="text-[15px] font-semibold text-slate-900">Connexion au serveur impossible</p>
                <p className="text-[13px] text-black/70 mt-1.5 max-w-sm">
                    Vous êtes toujours connecté : c&apos;est la liaison qui n&apos;a pas abouti.
                    Vérifiez votre réseau et réessayez.
                </p>
                <button type="button" data-testid="auth-reessayer"
                    onClick={() => { setState('checking'); setEssai(n => n + 1); }}
                    className="mt-5 h-11 px-5 rounded-lg bg-[#000091] hover:bg-[#1e40af] text-white text-[14px] font-semibold transition-colors">
                    Réessayer
                </button>
            </div>
        );
    }

    if (state !== 'authorized') {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-white">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#000091] rounded-full animate-spin mb-4"></div>
                <p className="text-sm text-slate-500 font-sans">Chargement...</p>
            </div>
        );
    }

    return <>{children}</>;
}
